/**
 * HomeConfig — the typed replacement for deploy/public/json/home.json.
 *
 * Bundled at build time (no fetch): AppController hands this object to
 * HomeController.setJSON during init, exactly where the JSON loader payload
 * used to arrive. Structure and values are a field-for-field copy of the
 * retired JSON, proven by a deep-equal parity script before the fetch path
 * was removed.
 *
 * - Drag/ease feel settings: documented inline below (mirrors HomeViewConfig).
 * - items: assembled in homeItems/index.ts — ORDER IS LOAD-BEARING there.
 * - regions: progress/mouse threshold regions consumed by
 *   HomeTimeline._createTimelineRegion -> TimelineRegionManager.
 */
import type { HomeViewConfig } from 'app/model/HomeViewConfig';
import { TimelineRegionType } from 'app/views/home/timeline/TimelineRegion';
import type { TimelineRegionParams } from 'app/views/home/timeline/TimelineRegion';
import { HOME_TIMELINE_ITEMS } from './homeItems';

/**
 * Timeline threshold regions (progress bands + mouse rectangles). The
 * onEnter/onExit callbacks and pixiContainer are attached at runtime by
 * HomeTimeline._createTimelineRegion.
 */
const HOME_REGIONS: TimelineRegionParams[] = [
	/**
	 * Intro dwell region — progress 0–0.14 keeps the home/logo state active.
	 */
	{
		id: 'Home',
		type: TimelineRegionType.PROGRESS,
		range: {
			min: 0.0,
			max: 0.14
		}
	},
	/**
	 * Mouse rectangle around the debug logo. `x2`/`w2` are inert leftovers
	 * (TimelineRegion only reads x/y/w/h).
	 */
	{
		id: 'HomeLogoArea',
		type: TimelineRegionType.RECTANGLE,
		rect: {
			x2: 0.05,
			x: 0,
			w2: 0.1,
			w: 0.2,
			y: 0.4,
			h: 0.2
		}
	},
	/**
	 * Screens dwell region (progress 0.3–0.5).
	 */
	{
		id: 'Screens',
		type: TimelineRegionType.PROGRESS,
		range: {
			min: 0.3,
			max: 0.5
		}
	},
	/**
	 * Disabled: min = max = 2 is outside the 0–1 progress range, so it can
	 * never intersect. Kept for parity.
	 */
	{
		id: 'SpacesTransition',
		type: TimelineRegionType.PROGRESS,
		range: {
			min: 2,
			max: 2
		}
	},
	/**
	 * Container-type region over the screens particle area. NOTE: container
	 * regions are currently inert (TimelineRegion._processContainer is empty).
	 */
	{
		id: 'ScreensParticles',
		type: TimelineRegionType.CONTAINER,
		rect: {
			x: 0.75433,
			y: 0.115,
			w: 0.09,
			h: 0.595
		}
	},
	/**
	 * Spaces dwell region (progress 0.6–0.8).
	 */
	{
		id: 'Spaces',
		type: TimelineRegionType.PROGRESS,
		range: {
			min: 0.6,
			max: 0.8
		}
	},
	/**
	 * Mouse rectangle over the spaces box.
	 */
	{
		id: 'SpacesBoxArea',
		type: TimelineRegionType.RECTANGLE,
		rect: {
			x: 0.75433,
			y: 0.1,
			w: 0.09,
			h: 0.3
		}
	},
	/**
	 * Container-type region over the spaces particle area (inert — see
	 * ScreensParticles).
	 */
	{
		id: 'SpacesParticles',
		type: TimelineRegionType.CONTAINER,
		rect: {
			x: 0.75433,
			y: 0.115,
			w: 0.09,
			h: 0.3
		}
	},
	/**
	 * Systems dwell region (progress 0.8–1).
	 */
	{
		id: 'Systems',
		type: TimelineRegionType.PROGRESS,
		range: {
			min: 0.8,
			max: 1
		}
	}
];

export const HomeConfig: HomeViewConfig = {
	dragEasePower: 2, //Ease power while dragging
	releaseEasePower: 15, //Ease power while not dragging

	velocityFactor: 1, //Factor that effects 'whoozy' style. Higher number more effect
	velocityFriction: 0.85, //Value that reduces the 'whoozy' styles on each frame

	//The amount the panel moves on drag: 0.8333 means the user drags ~83% of the
	//screen to move the container 100%:
	dragToMoveRatio: 0.8333333333,

	//Viewport-widths the top-level container scales to per device class:
	containerViewportsDesktop: 3,
	containerViewportsTablet: 4,
	containerViewportsMobile: 4,

	items: HOME_TIMELINE_ITEMS,

	//Legacy stray key from home.json — never consumed anywhere; kept for parity:
	cssElements: [{}],

	regions: HOME_REGIONS
};
